import { VenueRentalConfigurationType } from '@api';
import { formatDate, getDateFieldFormSchema } from '@common*';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { RentalConfigurationFormValuesType } from '../../types';

const TRANS_KEY = 'RENTAL_CONFIGURATION_TABLE.FORM.ERRORS';

const buildRentalConfigurationFormSchema = (
  t: TFunction<'venueDetailsRental'>
) =>
  z.object({
    validFrom: getDateFieldFormSchema({
      invalidFormatMessage: t(`${TRANS_KEY}.INVALID_FORMAT`),
      invalidDateMessage: t(`${TRANS_KEY}.INVALID_DATE`),
    }),
    validUntil: z
      .union([
        z.literal(''),
        getDateFieldFormSchema({
          invalidFormatMessage: t(`${TRANS_KEY}.INVALID_FORMAT`),
          invalidDateMessage: t(`${TRANS_KEY}.INVALID_DATE`),
        }),
      ])
      .nullable()
      .transform((v) => (v === '' ? null : v)),
    configuration: z.string().min(1, { message: t(`${TRANS_KEY}.REQUIRED`) }),
    version: z.string().nullable(),
    capacity: z.number().nullable(),
    rentalPercentageNet: z.number().nullable(),
    rentalPercentageGross: z.number().nullable(),
    rentalFlatFee: z.number().nullable(),
    minimumRental: z.number().nullable(),
    maximumRental: z.number().nullable(),
    fixedVenueSideCosts: z.number().nullable(),
    rentalSharePerTicket: z.number().nullable(),
    rentalShareBasis: z.number().nullable(),
    notes: z.string(),
    archived: z.boolean(),
  });

type RentalConfigurationFormSchemaType = ReturnType<
  typeof buildRentalConfigurationFormSchema
>;

type UseRentalConfigurationFormSchemaReturnType = {
  schema: RentalConfigurationFormSchemaType;
  defaultValues: RentalConfigurationFormValuesType;
};

export const useRentalConfigurationFormSchema = ({
  defaultRentalConfiguration,
  currentVersion,
}: {
  defaultRentalConfiguration?: VenueRentalConfigurationType;
  currentVersion: string;
}): UseRentalConfigurationFormSchemaReturnType => {
  const { t } = useTranslation('venueDetailsRental');

  const schema = buildRentalConfigurationFormSchema(t);

  const defaultValues: RentalConfigurationFormValuesType = {
    validFrom: formatDate(currentVersion),
    validUntil: defaultRentalConfiguration?.validUntil
      ? formatDate(defaultRentalConfiguration?.validUntil)
      : null,
    configuration: defaultRentalConfiguration?.configuration ?? '',
    version: defaultRentalConfiguration?.version ?? null,
    capacity: defaultRentalConfiguration?.capacity ?? null,
    rentalPercentageNet:
      defaultRentalConfiguration?.rentalPercentageNet ?? null,
    rentalPercentageGross:
      defaultRentalConfiguration?.rentalPercentageGross ?? null,
    rentalFlatFee: defaultRentalConfiguration?.rentalFlatFee?.value ?? null,
    minimumRental: defaultRentalConfiguration?.minimumRental?.value ?? null,
    maximumRental: defaultRentalConfiguration?.maximumRental?.value ?? null,
    fixedVenueSideCosts:
      defaultRentalConfiguration?.fixedVenueSideCosts?.value ?? null,
    rentalSharePerTicket:
      defaultRentalConfiguration?.rentalSharePerTicket?.value ?? null,
    rentalShareBasis: defaultRentalConfiguration?.rentalShareBasis ?? null,
    notes: defaultRentalConfiguration?.notes ?? '',
    archived: defaultRentalConfiguration?.archived ?? false,
  };

  return { schema, defaultValues };
};
